import React from 'react';
import { AlertCircle, Loader2, Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface PermissionChanges {
  added: string[];
  removed: string[];
}

export interface SaveConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleName: string;
  usersAffectedCount: number;
  totalPermissionsCount: number;
  permissionChanges: PermissionChanges;
  isSaving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const SaveConfirmationDialog: React.FC<SaveConfirmationDialogProps> = ({
  open,
  onOpenChange,
  roleName,
  usersAffectedCount,
  totalPermissionsCount,
  permissionChanges,
  isSaving,
  onConfirm,
  onCancel,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Confirm Role Changes</DialogTitle>
        <DialogDescription>Review the role configuration before saving</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2">
            <span className="text-sm font-medium">Role:</span>
            <span className="text-sm">{roleName}</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm font-medium">Users affected:</span>
            <span className="text-sm">{usersAffectedCount} {usersAffectedCount === 1 ? 'user' : 'users'}</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm font-medium">Total permissions enabled:</span>
            <span className="text-sm font-semibold text-primary">{totalPermissionsCount}</span>
          </div>
        </div>
        {(permissionChanges.added.length > 0 || permissionChanges.removed.length > 0) && (
          <>
            <Separator />
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {permissionChanges.added.map((label) => (
                <div key={`add-${label}`} className="flex items-center gap-2 text-sm text-success-700 dark:text-success-400">
                  <Plus className="h-4 w-4 shrink-0" />
                  <span>{label}</span>
                </div>
              ))}
              {permissionChanges.removed.map((label) => (
                <div key={`rm-${label}`} className="flex items-center gap-2 text-sm text-destructive dark:text-red-400">
                  <Minus className="h-4 w-4 shrink-0" />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </>
        )}
        <Separator />
        <div className="rounded-lg bg-blue-50 dark:bg-blue-950/20 p-3 border border-blue-200 dark:border-blue-800">
          <div className="flex gap-2">
            <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-900 dark:text-blue-100">
              These changes will be applied to all {usersAffectedCount} {usersAffectedCount === 1 ? 'user' : 'users'} with this role.
            </p>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={onConfirm} disabled={isSaving}>
          {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : 'Confirm & Save'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
